import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import ConsolBadge from '../components/ConsolBadge';
import { Select } from 'chakra-react-select';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Card,
  Stack,
  HStack,
  VStack,
  Badge,
  Button,
  Flex,
  Spacer,
  Input,
  Icon,
  Dialog,
  IconButton,
  Tabs,
  Popover,
} from '@chakra-ui/react';
import { FiFilter, FiSearch, FiRefreshCw, FiDownload, FiX, FiUserPlus, FiExternalLink } from 'react-icons/fi';
import { LuContainer, LuPackage, LuPlane, LuWrench, LuCheck, LuX, LuBuilding2, LuStore } from 'react-icons/lu';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import { dataService } from '../services/dataService';
import { branchDataService } from '../services/branchDataService';
import { exportToExcel, exportToCSV, formatDataForExport, exportToExcelStyled } from '../utils/exportUtils';
import { trackEvent } from '../services/posthogService';
import EditAgentModal from '../components/EditAgentModal';
import ViewAgentDetailsModal from '../components/ViewAgentDetailsModal';
import SendToQueueModal from '../components/SendToQueueModal';
import SuggestAgentModal from '../components/SuggestAgentModal';

const SERVICE_FILTER_OPTIONS = [
  { value: 'fcl', label: 'FCL' },
  { value: 'lcl', label: 'LCL' },
  { value: 'air', label: 'AIR' }
];

const NEGATIVE_SERVICE_VALUES = new Set(['', 'NO', '-', 'NOT AVAILABLE', 'N', 'FALSE', 'N/A', 'NA']);

const isServiceValueAvailable = (value) => {
  if (value === undefined || value === null) return false;
  const normalized = value.toString().trim().toUpperCase();
  if (!normalized) return false;
  return !NEGATIVE_SERVICE_VALUES.has(normalized);
};

const applyServiceFilter = (results, serviceKey) => {
  if (!serviceKey) return results;
  const field = serviceKey.toLowerCase();
  return results.filter((record) => isServiceValueAvailable(record[field]));
};

const JAKARTA_TIME_ZONE = 'Asia/Jakarta';

const Dashboard = () => {
  const { user, hasRole, canEditMain, canEditBranch, canViewMain } = useAuth();

  // Tab state: 'main' or 'branch' — User Branch defaults to 'branch'
  const [activeTab, setActiveTab] = useState(user?.role === 'User Branch' ? 'branch' : 'main');

  // const [searchQuery] = useState(''); // Keep for potential future use - currently unused

  // Modern dropdown search states
  const [countries, setCountries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [uploaders, setUploaders] = useState([]);
  // const [unifiedDropdownOpen, setUnifiedDropdownOpen] = useState(false); // Unused
  // const [unifiedSearch, setUnifiedSearch] = useState(''); // Unused - unified search not implemented
  const [selectedCountryFilter, setSelectedCountryFilter] = useState('');
  const [selectedAgentFilter, setSelectedAgentFilter] = useState('');
  const [selectedUploaderFilter, setSelectedUploaderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('approved');
  // const [focusedIndex, setFocusedIndex] = useState(-1); // Unused
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [rawSearchResults, setRawSearchResults] = useState([]);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [sendToQueueModalOpen, setSendToQueueModalOpen] = useState(false);
  const [suggestAgentModalOpen, setSuggestAgentModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [specialServicesModalOpen, setSpecialServicesModalOpen] = useState(false);
  const [selectedSpecialServices, setSelectedSpecialServices] = useState([]);

  // Searchable dropdown states - no longer needed with react-select
  // const [countrySearchTerm, setCountrySearchTerm] = useState('');
  // const [agentSearchTerm, setAgentSearchTerm] = useState('');
  // const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  // const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  // const [countryFocusedIndex, setCountryFocusedIndex] = useState(-1);
  // const [agentFocusedIndex, setAgentFocusedIndex] = useState(-1);
  // const [countryDropdownPosition, setCountryDropdownPosition] = useState({});
  // const [agentDropdownPosition, setAgentDropdownPosition] = useState({});



  // Export states
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportScope, setExportScope] = useState('current'); // 'current' or 'all'
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');

  // No longer needed with react-select
  // const [exportFormatDropdownOpen, setExportFormatDropdownOpen] = useState(false);
  // const [exportFormatFocusedIndex, setExportFormatFocusedIndex] = useState(-1);
  // const [exportScopeDropdownOpen, setExportScopeDropdownOpen] = useState(false);
  // const [exportScopeFocusedIndex, setExportScopeFocusedIndex] = useState(-1);
  // const [exportFormatResultsDropdownOpen, setExportFormatResultsDropdownOpen] = useState(false);
  // const [exportFormatResultsFocusedIndex, setExportFormatResultsFocusedIndex] = useState(-1);

  // Search results pagination states
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1);
  const [resultsItemsPerPage] = useState(9); // Always show 9 items per page (3 rows x 3 columns)

  // No longer needed with react-select - it doesn't require refs
  // const countryInputRef = useRef(null);
  // const agentInputRef = useRef(null);
  // const uploadDateInputRef = useRef(null);
  // const exportFormatRef = useRef(null);
  // const exportScopeRef = useRef(null);
  // const exportFormatResultsRef = useRef(null);

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        await Promise.all([loadCountries(), loadAgents(), loadUploaders()]);

        // Auto-load data with default status filter (approved)
        if (statusFilter !== 'all') {
          setLoading(true);
          try {
            const filters = { status: statusFilter };
            const service = activeTab === 'branch' ? branchDataService : dataService;
            const response = await service.searchCombined(filters);
            setRawSearchResults(response.data);
            const filteredResults = applyServiceFilter(response.data, selectedService);
            setSearchResults(filteredResults);
          } catch (error) {
            console.error('Error loading initial data with status filter:', error);
            setError('Failed to load data');
          } finally {
            setLoading(false);
          }
        }
      } catch (error) {
        setError("Failed to load initial data.");
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Reload when tab changes

  // Handle tab change - reset filters and reload data
  const handleTabChange = (details) => {
    const newTab = details.value;
    setActiveTab(newTab);
    // Reset filters when changing tabs
    setSelectedCountryFilter('');
    setSelectedAgentFilter('');
    setSelectedUploaderFilter('');
    setSelectedService('');
    setResultsCurrentPage(1);
  };

  // No longer needed with react-select - it handles filtering internally
  // const getFilteredCountries = useCallback(() => {
  //   if (!countrySearchTerm.trim()) return countries;
  //   return countries.filter(country =>
  //     country.toLowerCase().includes(countrySearchTerm.toLowerCase())
  //   );
  // }, [countrySearchTerm, countries]);

  // Calculate paginated search results
  const getPaginatedSearchResults = useCallback(() => {
    const startIndex = (resultsCurrentPage - 1) * resultsItemsPerPage;
    const endIndex = startIndex + resultsItemsPerPage;
    return searchResults.slice(startIndex, endIndex);
  }, [searchResults, resultsCurrentPage, resultsItemsPerPage]);

  const getTotalResultsPages = useCallback(() => {
    return Math.ceil(searchResults.length / resultsItemsPerPage);
  }, [searchResults, resultsItemsPerPage]);

  // No longer needed with react-select - it handles filtering internally
  // const getFilteredAgents = useCallback(() => {
  //   if (!agentSearchTerm.trim()) return agents;
  //   return agents.filter(agent =>
  //     agent.toLowerCase().includes(agentSearchTerm.toLowerCase())
  //   );
  // }, [agentSearchTerm, agents]);

  // No longer needed with react-select - it handles positioning internally
  // useEffect(() => {
  //   const handleResize = () => {
  //     if (countryDropdownOpen && countryInputRef.current) {
  //       const position = calculateDropdownPosition(countryInputRef.current, getFilteredCountries().length || countries.length);
  //       setCountryDropdownPosition(position);
  //     }
  //     if (agentDropdownOpen && agentInputRef.current) {
  //       const position = calculateDropdownPosition(agentInputRef.current, getFilteredAgents().length || agents.length);
  //       setAgentDropdownPosition(position);
  //     }
  //   };

  //   if (countryDropdownOpen || agentDropdownOpen) {
  //     handleResize();
  //     window.addEventListener('resize', handleResize);
  //     window.addEventListener('scroll', handleResize);
  //   }

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     window.removeEventListener('scroll', handleResize);
  //   };
  // }, [countryDropdownOpen, agentDropdownOpen, countries, agents, countrySearchTerm, agentSearchTerm]);

  // Get the appropriate service based on active tab
  const getActiveService = useCallback(() => {
    return activeTab === 'branch' ? branchDataService : dataService;
  }, [activeTab]);

  const loadCountries = async (agent = null) => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.getCountries(agent);
      setCountries(response.data);
    } catch (error) {
      console.error('Error loading countries:', error);
      throw error;
    }
  };

  const loadAgents = async (country = null) => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.getAgents(country);
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
      throw error;
    }
  };

  const loadUploaders = async (country = null, agent = null) => {
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.getUploaders(country, agent);
      setUploaders(response.data);
    } catch (error) {
      console.error('Error loading uploaders:', error);
      throw error;
    }
  };

  // Legacy search function - removed as not currently used
  // const handleSearch = (e) => {
  //   e.preventDefault();
  //   if (searchQuery.trim()) {
  //     window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
  //   }
  // };

  // Modern dropdown search functions - unused for now
  // const debounce = useCallback((func, delay) => {
  //   let timeoutId;
  //   return (...args) => {
  //     clearTimeout(timeoutId);
  //     timeoutId = setTimeout(() => func.apply(null, args), delay);
  //   };
  // }, []);

  // Unused - unified search not implemented yet
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // const handleUnifiedSearch = useCallback(debounce((value) => {
  //   if (!value.trim()) {
  //     setSelectedItem('');
  //     setSelectedItemType('');
  //     setSearchResults([]);
  //   }
  // }, 300), [debounce]);

  // const handleUnifiedInputChange = (value) => {
  //   setUnifiedSearch(value);
  //   setFocusedIndex(-1);
  //   handleUnifiedSearch(value);
  // };

  // const handleKeyDown = (e) => {
  //   const results = getUnifiedFilteredResults();
  //   const totalItems = results.countries.length + results.agents.length;

  //   if (!unifiedDropdownOpen || totalItems === 0) return;

  //   switch (e.key) {
  //     case 'ArrowDown':
  //       e.preventDefault();
  //       setFocusedIndex(prev => (prev + 1) % totalItems);
  //       break;
  //     case 'ArrowUp':
  //       e.preventDefault();
  //       setFocusedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
  //       break;
  //     case 'Enter':
  //       e.preventDefault();
  //       if (focusedIndex >= 0) {
  //         selectItemByIndex(focusedIndex);
  //       }
  //       break;
  //     case 'Escape':
  //       setUnifiedDropdownOpen(false);
  //       setFocusedIndex(-1);
  //       break;
  //     default:
  //       break;
  //   }
  // };

  // Unused - unified search not implemented yet
  // const selectItemByIndex = (index) => {
  //   const results = getUnifiedFilteredResults();
  //   if (index < results.countries.length) {
  //     handleUnifiedItemSelect(results.countries[index], 'country');
  //   } else {
  //     const agentIndex = index - results.countries.length;
  //     handleUnifiedItemSelect(results.agents[agentIndex], 'agent');
  //   }
  // };

  // Unused - unified search not implemented yet
  // const handleUnifiedItemSelect = async (item, type) => {
  //   setSelectedItem(item);
  //   setSelectedItemType(type);
  //   setUnifiedSearch('');
  //   // setUnifiedDropdownOpen(false);

  //   if (type === 'country') {
  //     await performCountrySearch(item);
  //   } else if (type === 'agent') {
  //     await performAgentSearch(item);
  //   }
  // };

  const performCombinedSearch = async (country, agent, uploader, serviceFilterValue = selectedService) => {
    setLoading(true);
    setError(null);
    resetResultsPagination(); // Reset pagination when doing new search
    try {
      const filters = {};
      if (country) filters.country = country;
      if (agent) filters.agent = agent;
      if (uploader) filters.uploader = uploader;
      if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;

      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchCombined(filters);
      console.log('[DEBUG Frontend] performCombinedSearch - response:', response.data.length, 'records');
      setRawSearchResults(response.data);
      const filteredResults = applyServiceFilter(response.data, serviceFilterValue);
      setSearchResults(filteredResults);
      trackEvent('search', { type: 'combined', country, agent, uploader, results: response.data.length, tab: activeTab });
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
      setRawSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const performCountrySearch = async (country, serviceFilterValue = selectedService) => {
    setLoading(true);
    setError(null);
    resetResultsPagination(); // Reset pagination when doing new search
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchByCountry(country);
      console.log('[DEBUG Frontend] performCountrySearch - response:', response.data.length, 'records');
      setRawSearchResults(response.data);
      const filteredResults = applyServiceFilter(response.data, serviceFilterValue);
      setSearchResults(filteredResults);
      trackEvent('search', { type: 'country', country, results: response.data.length, tab: activeTab });
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
      setRawSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const performAgentSearch = async (agent, serviceFilterValue = selectedService) => {
    setLoading(true);
    setError(null);
    resetResultsPagination(); // Reset pagination when doing new search
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchByAgent(agent);
      console.log('[DEBUG Frontend] performAgentSearch - response:', response.data.length, 'records');
      setRawSearchResults(response.data);
      const filteredResults = applyServiceFilter(response.data, serviceFilterValue);
      setSearchResults(filteredResults);
      trackEvent('search', { type: 'agent', agent, results: response.data.length, tab: activeTab });
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
      setRawSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const performUploaderSearch = async (uploader, serviceFilterValue = selectedService) => {
    setLoading(true);
    setError(null);
    resetResultsPagination(); // Reset pagination when doing new search
    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.searchByUploader(uploader);
      console.log('[DEBUG Frontend] performUploaderSearch - response:', response.data.length, 'records');
      setRawSearchResults(response.data);
      const filteredResults = applyServiceFilter(response.data, serviceFilterValue);
      setSearchResults(filteredResults);
      trackEvent('search', { type: 'uploader', uploader, results: response.data.length, tab: activeTab });
    } catch (error) {
      console.error('Error searching:', error);
      setError('Failed to search data');
      setSearchResults([]);
      setRawSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAgentsWithService = async (serviceKey) => {
    setLoading(true);
    setError(null);
    resetResultsPagination();
    try {
      const response = await dataService.getAllData();
      setRawSearchResults(response.data);
      const filteredResults = applyServiceFilter(response.data, serviceKey);
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error fetching agents by service:', error);
      setError('Failed to search data');
      setSearchResults([]);
      setRawSearchResults([]);
    } finally {
      setLoading(false);
    }
  };


  // const handleCountryFilterChange = async (country) => {
  //   if (country) {
  //     setSelectedItem(country);
  //     setSelectedItemType('country');
  //     await performCountrySearch(country);
  //   } else {
  //     clearAllFilters();
  //   }
  // };

  // const handleAgentFilterChange = async (agent) => {
  //   if (agent) {
  //     setSelectedItem(agent);
  //     setSelectedItemType('agent');
  //     await performAgentSearch(agent);
  //   } else {
  //     clearAllFilters();
  //   }
  // };

  // Unused - unified search not implemented yet
  // const getUnifiedFilteredResults = () => {
  //   const searchTerm = unifiedSearch.toLowerCase();
  //   if (!searchTerm) return { countries: [], agents: [] };

  //   const filteredCountriesUnified = countries.filter(country =>
  //     country.toLowerCase().includes(searchTerm)
  //   ).slice(0, 5);

  //   const filteredAgentsUnified = agents.filter(agent =>
  //     agent.toLowerCase().includes(searchTerm)
  //   ).slice(0, 5);

  //   return {
  //     countries: filteredCountriesUnified,
  //     agents: filteredAgentsUnified
  //   };
  // };

  // Enhanced status badge functions with case-insensitive processing and uppercase display
  // const getStatusBadge = (status) => {
  //   if (!status || status.toString().trim() === '') return null;

  //   // Always convert to uppercase for consistent display
  //   const normalizedStatus = status.toString().trim().toUpperCase();

  //   const statusConfig = {
  //     'APPROVED': { color: 'green', icon: '✅' },
  //     'POTENTIAL': { color: 'yellow', icon: '⚠️' }
  //   };

  //   const config = statusConfig[normalizedStatus] || { color: 'gray', icon: '❓' };

  //   return (
  //     <span className={`status-badge status-${config.color}`}>
  //       {config.icon} {normalizedStatus}
  //     </span>
  //   );
  // };

  // const getServiceBadge = (service, defaultText = 'NOT SET') => {
  //   if (!service || service.toString().trim() === '' || service === '-') {
  //     return (
  //       <span className="status-badge status-red">
  //         ❌ NOT AVAILABLE
  //       </span>
  //     );
  //   }

  //   // Always convert to uppercase for consistent display
  //   const normalizedService = service.toString().trim().toUpperCase();

  //   const serviceConfig = {
  //     'YES': { color: 'green', icon: '✅' },
  //     'AVAILABLE': { color: 'green', icon: '✅' },
  //     'TRUE': { color: 'green', icon: '✅' },
  //     'NO': { color: 'red', icon: '❌' },
  //     'NOT AVAILABLE': { color: 'red', icon: '❌' },
  //     'FALSE': { color: 'red', icon: '❌' },
  //     'PENDING': { color: 'yellow', icon: '⏳' },
  //     'NOT SET': { color: 'gray', icon: '❓' }
  //   };

  //   const config = serviceConfig[normalizedService] || { color: 'green', icon: '✅' };

  //   return (
  //     <span className={`status-badge status-${config.color}`}>
  //       {config.icon} {normalizedService}
  //     </span>
  //   );
  // };

  // const getContractBadge = (contract) => {
  //   if (!contract || contract.toString().trim() === '') return null;

  //   // Always convert to uppercase for consistent display
  //   const normalizedContract = contract.toString().trim().toUpperCase();

  //   const contractConfig = {
  //     'YES': { color: 'green', icon: '✅' },
  //     'NO': { color: 'red', icon: '❌' },
  //     'PENDING': { color: 'yellow', icon: '⏳' }
  //   };

  //   const config = contractConfig[normalizedContract] || { color: 'gray', icon: '❓' };

  //   return (
  //     <span className={`status-badge contract-${config.color}`}>
  //       {config.icon} {normalizedContract}
  //     </span>
  //   );
  // };

  // const getAdditionalBadge = (additional) => {
  //   if (!additional || additional.toString().trim() === '') return null;

  //   // Always convert to uppercase for consistent display
  //   const normalizedAdditional = additional.toString().trim().toUpperCase();

  //   const additionalConfig = {
  //     'AGENT CONSIGNED': { color: 'green', icon: '🏢' },
  //     'QTM CONSIGNED': { color: 'blue', icon: '📋' },
  //     'BOTH SIDE': { color: 'purple', icon: '🤝' }
  //   };

  //   const config = additionalConfig[normalizedAdditional] || { color: 'gray', icon: '❓' };

  //   return (
  //     <span className={`status-badge additional-${config.color}`}>
  //       {config.icon} {normalizedAdditional}
  //     </span>
  //   );
  // };

  // NOTE: Auto-detection functions below are deprecated
  // Now using manually configured strongest_service from database instead

  // // Helper function to get service strength score
  // const getServiceStrength = (serviceValue) => {
  //   const normalizedValue = serviceValue ? serviceValue.toString().trim().toUpperCase() : '';

  //   // Not available
  //   if (!normalizedValue || normalizedValue === '' || normalizedValue === '-' || normalizedValue === 'NULL') {
  //     return 0;
  //   }

  //   // Simple yes/no markers
  //   if (['YES', 'AVAILABLE', 'TRUE', 'Y', 'X'].includes(normalizedValue)) {
  //     return 1;
  //   }

  //   // Detailed description - longer text means more detailed service info
  //   return 2 + (normalizedValue.length / 10);
  // };

  // // Determine strongest service for an agent
  // const getStrongestService = (agent) => {
  //   const fclStrength = getServiceStrength(agent.fcl);
  //   const lclStrength = getServiceStrength(agent.lcl);
  //   const airStrength = getServiceStrength(agent.air);

  //   const maxStrength = Math.max(fclStrength, lclStrength, airStrength);

  //   // Return null if no services available
  //   if (maxStrength === 0) return null;

  //   // Return the service name with highest strength
  //   if (fclStrength === maxStrength) return 'FCL';
  //   if (lclStrength === maxStrength) return 'LCL';
  //   if (airStrength === maxStrength) return 'AIR';
  //   return null;
  // };

  const getServiceAvailability = (serviceValue) => {
    const normalizedValue = serviceValue ? serviceValue.toString().trim().toUpperCase() : '';

    return normalizedValue &&
      normalizedValue !== '' &&
      normalizedValue !== '-' &&
      normalizedValue !== 'NULL' &&
      (
        ['YES', 'AVAILABLE', 'TRUE', 'Y'].includes(normalizedValue) ||
        ['X'].includes(normalizedValue) ||
        normalizedValue.includes('FCL') ||
        normalizedValue.includes('LCL') ||
        normalizedValue.includes('AIR') ||
        normalizedValue.includes('FREIGHT') ||
        normalizedValue.includes('CARGO') ||
        normalizedValue.includes('SEA') ||
        normalizedValue.includes('REEFER') ||
        normalizedValue.includes('WAREHOUSING')
      );
  };

  // Compact badge functions for horizontal layout
  const getCompactServiceBadge = (serviceName, serviceValue, icon, isStrongest = false) => {
    const isAvailable = getServiceAvailability(serviceValue);
    const colorPalette = isAvailable ? 'green' : 'red';
    const statusIcon = isAvailable ? <Icon as={LuCheck} boxSize="14px" /> : <Icon as={LuX} boxSize="14px" />;

    return (
      <Badge
        colorPalette={colorPalette}
        variant={isAvailable ? 'solid' : 'subtle'}
        border={isAvailable ? undefined : 'none'}
        px={2}
        py={1}
        borderRadius="md"
        fontSize="xs"
        display="inline-flex"
        alignItems="center"
        gap={1}
        title={`${serviceName}: ${isAvailable ? 'Available' : 'Not Available'}${isStrongest ? ' (Strongest Service)' : ''}`}
        boxShadow={isStrongest && isAvailable ? '0 0 10px 3px rgba(234, 179, 8, 0.8), inset 0 0 5px rgba(234, 179, 8, 0.3)' : 'none'}
        outline={isStrongest && isAvailable ? '2px solid' : 'none'}
        outlineColor={isStrongest && isAvailable ? 'yellow.400' : 'transparent'}
        outlineOffset={isStrongest && isAvailable ? '1px' : '0'}
      >
        <Icon as={icon} boxSize="14px" /> {serviceName} {statusIcon}
        {isStrongest && isAvailable && <Text as="span" ml={1}>⭐</Text>}
      </Badge>
    );
  };

  // Handle special services click to show popup
  const handleSpecialServicesClick = (specialServicesString) => {
    if (!specialServicesString || specialServicesString.trim() === '') {
      setSelectedSpecialServices([]);
    } else {
      // Strictly use semicolon delimiter
      const services = specialServicesString.split(';').map(s => s.trim()).filter(s => s);
      setSelectedSpecialServices(services);
    }
    setSpecialServicesModalOpen(true);
  };

  // Get special service badge - single badge with click to view details
  const getSpecialServiceBadge = (specialServicesString) => {
    const hasServices = specialServicesString && specialServicesString.trim() !== '';
    let services = [];

    if (hasServices) {
      // Strictly use semicolon delimiter
      services = specialServicesString.split(';').map(s => s.trim()).filter(s => s);
    }

    const colorPalette = hasServices && services.length > 0 ? 'purple' : 'gray';
    const serviceCount = services.length;

    return (
      <Badge
        colorPalette={colorPalette}
        variant="subtle"
        px={2}
        py={1}
        borderRadius="full"
        fontSize="xs"
        cursor="pointer"
        display="inline-flex"
        alignItems="center"
        gap={1}
        title={hasServices ? `Special Services (${serviceCount}): Click to view details` : 'Special Service: Not Available'}
        onClick={() => handleSpecialServicesClick(specialServicesString)}
        _hover={{ opacity: 0.8 }}
      >
        <Icon as={LuWrench} boxSize="14px" /> Special {hasServices ? `(${serviceCount})` : ''}
      </Badge>
    );
  };

  // Badge functions removed as STATUS, CONTRACT, and ADDITIONAL are now shown in details modal only
  // const getCompactStatusBadge = (label, status, icon) => { ... };
  // const getCompactContractBadge = (label, contract, icon) => { ... };
  // const getCompactAdditionalBadge = (label, additional, icon) => { ... };


  // const isFieldAvailable = (value) => {
  //   return value && value.trim() !== '' && value !== '-';
  // };

  // Handle view details
  const handleViewDetails = (agent) => {
    setSelectedAgent(agent);
    setViewModalOpen(true);
  };

  // Handle edit agent
  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setEditModalOpen(true);
  };

  // Handle save after edit
  const handleSaveAgent = (updatedAgent) => {
    // Update the agent in search results
    setSearchResults(prev =>
      prev.map(agent =>
        agent.id === updatedAgent.id ? updatedAgent : agent
      )
    );
    setRawSearchResults(prev =>
      prev.map(agent =>
        agent.id === updatedAgent.id ? updatedAgent : agent
      )
    );
  };

  // Handle toggle favorite (Editor and Admin only)
  const handleToggleFavorite = async (agent, e) => {
    // Prevent event bubbling to parent elements
    e.stopPropagation();

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      const response = await service.toggleFavorite(agent.id, !agent.is_favorite);

      // Update the agent in search results with new favorite status
      setSearchResults(prev =>
        prev.map(item =>
          item.id === agent.id ? { ...item, is_favorite: response.data.agent.is_favorite } : item
        )
      );
      setRawSearchResults(prev =>
        prev.map(item =>
          item.id === agent.id ? { ...item, is_favorite: response.data.agent.is_favorite } : item
        )
      );
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Failed to toggle favorite status');
    }
  };

  // Handle delete agent
  const handleDeleteAgent = async (agent) => {
    const agentName = agent.agent_name || 'this agent';
    const country = agent.country || 'Unknown Country';

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${agentName} (${country})?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.deleteAgent(agent.id);

      // Remove the agent from search results
      setSearchResults(prev =>
        prev.filter(a => a.id !== agent.id)
      );
      setRawSearchResults(prev =>
        prev.filter(a => a.id !== agent.id)
      );

      alert(`Agent "${agentName}" has been successfully deleted.`);
    } catch (error) {
      console.error('Error deleting agent:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete agents. Only admins can delete agents.');
      } else {
        alert(`Failed to delete agent: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      }
    }
  };

  // Handle send to queue - open modal
  const handleSendToQueue = (agent) => {
    setSelectedAgent(agent);
    setSendToQueueModalOpen(true);
  };

  // Handle confirm send to queue from modal
  const handleConfirmSendToQueue = async (queueData) => {
    const agentName = selectedAgent.agent_name || 'this agent';

    try {
      const service = activeTab === 'branch' ? branchDataService : dataService;
      await service.sendToQueue(selectedAgent.id, queueData);

      // Remove the agent from search results
      setSearchResults(prev =>
        prev.filter(a => a.id !== selectedAgent.id)
      );
      setRawSearchResults(prev =>
        prev.filter(a => a.id !== selectedAgent.id)
      );

      setSendToQueueModalOpen(false);
      setSelectedAgent(null);
      alert(`Agent "${agentName}" has been sent to the pending queue with status: ${queueData.queue_status}`);
    } catch (error) {
      console.error('Error sending agent to queue:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to send agents to queue. Only editors and admins can do this.');
      } else if (error.response?.status === 400) {
        alert(`Validation error: ${error.response?.data?.error || 'Invalid data'}`);
      } else {
        alert(`Failed to send agent to queue: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleCloseSendToQueueModal = () => {
    setSendToQueueModalOpen(false);
    setSelectedAgent(null);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAgent(null);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedAgent(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: JAKARTA_TIME_ZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // const unifiedResults = getUnifiedFilteredResults();


  // No longer needed with react-select - it handles all this internally
  // const highlightMatch = (text, searchTerm) => {
  //   if (!searchTerm.trim()) return text;
  //   const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  //   const parts = text.split(regex);
  //   return parts.map((part, index) => {
  //     if (part.toLowerCase() === searchTerm.toLowerCase()) {
  //       return <mark key={index} className="highlight">{part}</mark>;
  //     }
  //     return part;
  //   });
  // };

  // const calculateDropdownPosition = (inputElement, optionsCount = 10) => {
  //   if (!inputElement) return {};

  //   // Estimate dropdown height (52px per option + padding)
  //   const estimatedDropdownHeight = Math.min(optionsCount * 52 + 20, 300);
  //   const viewportHeight = window.innerHeight;
  //   const rect = inputElement.getBoundingClientRect();

  //   // Check if dropdown would go below viewport
  //   const spaceBelow = viewportHeight - rect.bottom;
  //   const spaceAbove = rect.top;
  //   const shouldOpenUpward = spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;

  //   return {
  //     openUpward: shouldOpenUpward
  //   };
  // };

  // const handleCountryInputChange = (value) => {
  //   setCountrySearchTerm(value);
  //   setCountryFocusedIndex(-1);
  //   if (!countryDropdownOpen) {
  //     const position = calculateDropdownPosition(countryInputRef.current, getFilteredCountries().length);
  //     setCountryDropdownPosition(position);
  //     setCountryDropdownOpen(true);
  //   }
  // };

  // const handleAgentInputChange = (value) => {
  //   setAgentSearchTerm(value);
  //   setAgentFocusedIndex(-1);
  //   if (!agentDropdownOpen) {
  //     const position = calculateDropdownPosition(agentInputRef.current, getFilteredAgents().length);
  //     setAgentDropdownPosition(position);
  //     setAgentDropdownOpen(true);
  //   }
  // };

  const handleCountrySelect = async (option) => {
    const country = option ? option.value : '';
    setSelectedCountryFilter(country);

    if (!country && !selectedAgentFilter && !selectedUploaderFilter) {
      // No filters selected, clear results
      resetResultsPagination();
      setSearchResults([]);
      setRawSearchResults([]);
      setLoading(false);
      setError(null);
      await Promise.all([loadCountries(), loadAgents(), loadUploaders()]);
      if (selectedService) {
        await fetchAllAgentsWithService(selectedService);
      }
      return;
    }

    // Reload agents and uploaders filtered by this country
    await Promise.all([loadAgents(country), loadUploaders(country, selectedAgentFilter)]);
    // Perform combined search with all active filters
    await performCombinedSearch(country, selectedAgentFilter, selectedUploaderFilter, selectedService);
  };

  const handleAgentSelect = async (option) => {
    const agent = option ? option.value : '';
    setSelectedAgentFilter(agent);

    if (!selectedCountryFilter && !agent && !selectedUploaderFilter) {
      // No filters selected, clear results
      resetResultsPagination();
      setSearchResults([]);
      setRawSearchResults([]);
      setLoading(false);
      setError(null);
      await Promise.all([loadCountries(), loadAgents(), loadUploaders()]);
      if (selectedService) {
        await fetchAllAgentsWithService(selectedService);
      }
      return;
    }

    // Reload countries and uploaders filtered by this agent
    await Promise.all([loadCountries(agent), loadUploaders(selectedCountryFilter, agent)]);
    // Perform combined search with all active filters
    await performCombinedSearch(selectedCountryFilter, agent, selectedUploaderFilter, selectedService);
  };

  const handleUploaderSelect = async (option) => {
    const uploader = option ? option.value : '';
    setSelectedUploaderFilter(uploader);

    if (!selectedCountryFilter && !selectedAgentFilter && !uploader) {
      // No filters selected, clear results
      resetResultsPagination();
      setSearchResults([]);
      setRawSearchResults([]);
      setLoading(false);
      setError(null);
      await Promise.all([loadCountries(), loadAgents(), loadUploaders()]);
      if (selectedService) {
        await fetchAllAgentsWithService(selectedService);
      }
      return;
    }

    // Perform combined search with all active filters
    await performCombinedSearch(selectedCountryFilter, selectedAgentFilter, uploader, selectedService);
  };

  const handleServiceSelect = async (option) => {
    const serviceValue = option ? option.value : '';
    setSelectedService(serviceValue);
    resetResultsPagination();

    // If any filter is active, perform combined search with new service filter
    if (selectedCountryFilter || selectedAgentFilter || selectedUploaderFilter) {
      await performCombinedSearch(selectedCountryFilter, selectedAgentFilter, selectedUploaderFilter, serviceValue);
      return;
    }

    // No entity filters active, check if we have raw results to filter
    if (!serviceValue) {
      setSearchResults([]);
      setRawSearchResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (rawSearchResults.length > 0) {
      const filteredResults = applyServiceFilter(rawSearchResults, serviceValue);
      setSearchResults(filteredResults);
      setLoading(false);
      setError(null);
      return;
    }

    // No filters and no raw results, fetch all agents with this service
    await fetchAllAgentsWithService(serviceValue);
  };

  // No longer needed with react-select - it handles keyboard navigation internally
  // const handleCountryKeyDown = (e) => {
  //   const filtered = getFilteredCountries();
  //   if (e.key === 'ArrowDown') {
  //     e.preventDefault();
  //     setCountryFocusedIndex(prev =>
  //       prev < filtered.length - 1 ? prev + 1 : 0
  //     );
  //   } else if (e.key === 'ArrowUp') {
  //     e.preventDefault();
  //     setCountryFocusedIndex(prev =>
  //       prev > 0 ? prev - 1 : filtered.length - 1
  //     );
  //   } else if (e.key === 'Enter') {
  //     e.preventDefault();
  //     if (countryFocusedIndex >= 0 && filtered[countryFocusedIndex]) {
  //       handleCountrySelect(filtered[countryFocusedIndex]);
  //     }
  //   } else if (e.key === 'Escape') {
  //     setCountryDropdownOpen(false);
  //     setCountryFocusedIndex(-1);
  //   }
  // };

  // const handleAgentKeyDown = (e) => {
  //   const filtered = getFilteredAgents();
  //   if (e.key === 'ArrowDown') {
  //     e.preventDefault();
  //     setAgentFocusedIndex(prev =>
  //       prev < filtered.length - 1 ? prev + 1 : 0
  //     );
  //   } else if (e.key === 'ArrowUp') {
  //     e.preventDefault();
  //     setAgentFocusedIndex(prev =>
  //       prev > 0 ? prev - 1 : filtered.length - 1
  //     );
  //   } else if (e.key === 'Enter') {
  //     e.preventDefault();
  //     if (agentFocusedIndex >= 0 && filtered[agentFocusedIndex]) {
  //       handleAgentSelect(filtered[agentFocusedIndex]);
  //     }
  //   } else if (e.key === 'Escape') {
  //     setAgentDropdownOpen(false);
  //     setAgentFocusedIndex(-1);
  //   }
  // };

  // const handleUploadDateFilterChange = async (uploadDate) => {
  //   if (uploadDate) {
  //     setSelectedUploadDate(uploadDate);
  //     // Re-run current search with upload date filter
  //     if (selectedItem && selectedItemType === 'country') {
  //       await performCountrySearch(selectedItem);
  //     } else if (selectedItem && selectedItemType === 'agent') {
  //       await performAgentSearch(selectedItem);
  //     }
  //   } else {
  //     clearAllFilters();
  //   }
  // };

  // No longer needed with react-select
  // const handleUploadDateKeyDown = (e) => { ... };
  // const getUploadDateLabel = () => { ... };

  const clearAllFilters = async () => {
    setSelectedCountryFilter('');
    setSelectedAgentFilter('');
    setSelectedUploaderFilter('');
    setSelectedService('');
    setSearchResults([]);
    setRawSearchResults([]);
    resetResultsPagination(); // Reset results pagination
    // Reset loading and error states to prevent stale UI
    setLoading(false);
    setError(null);
    // Reload all countries, agents, and uploaders without filters
    await Promise.all([loadCountries(), loadAgents(), loadUploaders()]);
  };

  // Search results pagination handlers
  const handleResultsPageChange = (newPage) => {
    setResultsCurrentPage(newPage);
  };

  const handleResultsPrevPage = () => {
    if (resultsCurrentPage > 1) {
      handleResultsPageChange(resultsCurrentPage - 1);
    }
  };

  const handleResultsNextPage = () => {
    const totalPages = getTotalResultsPages();
    if (resultsCurrentPage < totalPages) {
      handleResultsPageChange(resultsCurrentPage + 1);
    }
  };

  // Reset results pagination when doing new search
  const resetResultsPagination = () => {
    setResultsCurrentPage(1);
  };

  // Export format dropdown handlers
  const exportFormatOptions = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' }
  ];

  const handleExportFormatSelect = (option) => {
    if (option) {
      setExportFormat(option.value);
    }
  };

  // No longer needed with react-select
  // const handleExportFormatKeyDown = (e) => { ... };
  // const getExportFormatLabel = (value) => { ... };

  // Export scope dropdown handlers (for results section)
  const getExportScopeOptions = () => [
    { value: 'current', label: `📋 Current View (${searchResults.length} records)` },
    { value: 'all', label: '🌐 All Data' }
  ];

  const handleExportScopeSelect = (option) => {
    if (option) {
      setExportScope(option.value);
    }
  };

  // No longer needed with react-select
  // const handleExportScopeKeyDown = (e) => { ... };
  // const getExportScopeLabel = (value) => { ... };

  // Export format dropdown handlers (for results section)
  const handleExportFormatResultsSelect = (option) => {
    if (option) {
      setExportFormat(option.value);
    }
  };

  // No longer needed with react-select
  // const handleExportFormatResultsKeyDown = (e) => { ... };

  // Progress callback for large exports
  const handleExportProgress = (progress, message) => {
    setExportProgress(progress);
    setExportMessage(message);
  };

  // Export handler function
  const handleExport = async () => {
    // Validate export scope
    if (exportScope === 'current' && searchResults.length === 0) {
      alert('No data to export. Please perform a search first or select "Export All Data".');
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    setExportMessage('Initializing export...');

    try {
      let dataToExport;
      let filename;
      const timestamp = new Date().toISOString().split('T')[0];
      if (exportScope === 'current') {
        // Export current view
        setExportMessage('Preparing current view data...');
        dataToExport = searchResults;
        const filenameSegments = [];
        if (selectedCountryFilter) {
          filenameSegments.push(`Country_${selectedCountryFilter.replace(/[^a-z0-9]/gi, '_')}`);
        }
        if (selectedAgentFilter) {
          filenameSegments.push(`Agent_${selectedAgentFilter.replace(/[^a-z0-9]/gi, '_')}`);
        }
        if (selectedUploaderFilter) {
          filenameSegments.push(`Uploader_${selectedUploaderFilter.replace(/[^a-z0-9]/gi, '_')}`);
        }
        if (selectedServiceOption) {
          filenameSegments.push(`Service_${selectedServiceOption.label.replace(/[^a-z0-9]/gi, '_')}`);
        }
        const filenameSuffix = filenameSegments.length > 0 ? filenameSegments.join('_') : 'Current_View';
        filename = `Agent_Data_${filenameSuffix}_${timestamp}`;
      } else {
        // Export all data with City/Port PIC info
        setExportMessage('Fetching all data from server...');
        const response = await dataService.getExportAllData();
        dataToExport = response.data;

        if (!dataToExport || dataToExport.length === 0) {
          alert('No data available to export.');
          setIsExporting(false);
          return;
        }

        filename = `Agent_Data_All_${timestamp}`;

        setExportMessage(`Preparing ${dataToExport.length} records for export...`);
      }

      // Fetch City/Port data for export enrichment
      let allCityPortPics = [];
      try {
        const [cpResponse, mappingsResponse] = await Promise.all([
          axios.get(`${config.BACKEND_URL}/api/city-ports`, { withCredentials: true }),
          axios.get(`${config.BACKEND_URL}/api/city-ports/agent-mappings`, { withCredentials: true }),
        ]);
        allCityPortPics = cpResponse.data;
        const agentCityPortMappings = mappingsResponse.data; // { agent_id: "city names" }

        // Enrich records: fill city_port from junction table if NULL
        dataToExport = dataToExport.map(record => {
          if (!record.city_port && record.id && agentCityPortMappings[record.id]) {
            return { ...record, city_port: agentCityPortMappings[record.id] };
          }
          return record;
        });
      } catch (err) {
        console.warn('Could not fetch city port data:', err);
      }

      // Export based on selected format with progress tracking
      let success = false;
      if (exportFormat === 'xlsx') {
        // Use styled ExcelJS export (purple headers, borders, YES/NO colors)
        success = await exportToExcelStyled(dataToExport, filename, handleExportProgress, allCityPortPics);
      } else if (exportFormat === 'csv') {
        const formattedData = formatDataForExport(dataToExport);
        success = exportToCSV(formattedData, filename, handleExportProgress);
      }

      if (success) {
        trackEvent('export', { format: exportFormat, scope: exportScope, records: dataToExport.length, tab: activeTab });
        setExportMessage('Export completed successfully!');
        setTimeout(() => {
          alert(`${dataToExport.length} records exported successfully as ${exportFormat.toUpperCase()}!`);
        }, 100);
      } else {
        alert('Export failed. Please try again.');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(`An error occurred during export: ${error.message || 'Unknown error'}`);
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setExportMessage('');
      }, 1000);
    }
  };

  const selectedServiceOption = selectedService
    ? SERVICE_FILTER_OPTIONS.find(option => option.value === selectedService)
    : null;

  const hasActiveFilters = Boolean(
    selectedCountryFilter ||
    selectedAgentFilter ||
    selectedUploaderFilter ||
    selectedServiceOption
  );

  return (
    <Box minH="100vh" bg="gray.50" p={0}>
      {/* Premium Hero Section with Glassmorphism */}
      <Box
        position="relative"
        overflow="hidden"
        pt={12}
        pb={24}
        px={8}
        mb={-12}
        roundedBottom="3xl"
        shadow="2xl"
        style={{
          background: 'linear-gradient(135deg, #8B3BA8 0%, #6C2888 40%, #5a2272 70%, #481c5c 100%)',
        }}
      >
        {/* Animated Background Orbs */}
        <Box
          position="absolute"
          top="-50%"
          left="-20%"
          width="60%"
          height="200%"
          borderRadius="full"
          opacity={0.15}
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
          }}
        />
        <Box
          position="absolute"
          bottom="-30%"
          right="-10%"
          width="50%"
          height="150%"
          borderRadius="full"
          opacity={0.1}
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, transparent 60%)',
          }}
        />

        <Container maxW="container.xl" position="relative" zIndex={1}>
          <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="space-between" gap={6}>
            {/* Left: Welcome Text */}
            <Box>
              <Badge
                colorPalette="whiteAlpha"
                variant="subtle"
                px={4}
                py={1}
                borderRadius="full"
                mb={4}
                fontSize="sm"
                fontWeight="semibold"
                bg="whiteAlpha.200"
                color="white"
              >
                🌍 Agent Management System
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                fontWeight="bold"
                letterSpacing="tight"
                mb={3}
                color="white"
                textShadow="0 2px 10px rgba(0,0,0,0.2)"
              >
                Welcome to AGENT DIRECTORY
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.900" fontWeight="medium">
                Streamline your logistics network management
              </Text>
              <Button
                as="a"
                href="https://quantermlogisitcsvietnam-my.sharepoint.com/:w:/g/personal/oversea12_hcm_quanterm_com_vn/IQC9u_UVpeQySb5i-W0JL8KUAcD4ByRsO8h-kRxyxHPYvlc?e=tCYw88"
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                mt={4}
                variant="outline"
                borderColor="whiteAlpha.500"
                color="white"
                leftIcon={<FiExternalLink />}
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label="Open User Guide"
              >
                User Guide
              </Button>
            </Box>

            {/* Right: User Greeting Card with Glassmorphism */}
            <Box
              bg="whiteAlpha.200"
              backdropFilter="blur(16px)"
              borderRadius="2xl"
              p={6}
              border="1px solid"
              borderColor="whiteAlpha.300"
              boxShadow="0 8px 32px rgba(0,0,0,0.15)"
              minW={{ base: 'full', md: '280px' }}
            >
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="whiteAlpha.800" fontWeight="medium">
                  Hello,
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="white" noOfLines={1}>
                  {user?.displayName || user?.email || 'User'}
                </Text>
                <Badge
                  colorPalette="green"
                  variant="solid"
                  fontSize="xs"
                  mt={1}
                  borderRadius="full"
                >
                  ● Online
                </Badge>
                {(user?.role === 'User' || user?.role === 'Admin') && (
                  <Button
                    size="sm"
                    colorPalette="whiteAlpha"
                    variant="outline"
                    mt={3}
                    onClick={() => setSuggestAgentModalOpen(true)}
                    borderColor="whiteAlpha.400"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  >
                    <FiUserPlus style={{ marginRight: '6px' }} />
                    Suggest Agent
                  </Button>
                )}
              </VStack>
            </Box>
          </Flex>
        </Container>
      </Box>

      <Container maxW="container.xl" mt={-10}>
        {/* Data Type Tabs: Main vs Branch */}
        <Tabs.Root
          value={activeTab}
          onValueChange={handleTabChange}
          mb={6}
          sx={{
            '[data-part="indicator"]': { display: 'none' },
            '[data-part="list"]': { borderBottom: 'none' },
            '[data-part="trigger"]': { borderBottom: 'none' },
            '[data-part="trigger"][data-state="active"]': { borderBottom: 'none', boxShadow: 'none' }
          }}
        >
          <Tabs.List bg="white" borderRadius="lg" shadow="md" p={1} border="none" borderBottom="none" borderColor="transparent" boxShadow="none">
            {canViewMain() && <Tabs.Trigger
              value="main"
              px={6}
              py={3}
              fontWeight="semibold"
              borderRadius="md"
              borderBottom="none"
              borderBottomWidth={0}
              borderBottomColor="transparent"
              boxShadow="none"
              _selected={{
                bg: 'purple.500',
                color: 'white',
                borderBottom: 'none',
                borderBottomWidth: 0,
                borderBottomColor: 'transparent',
                boxShadow: 'none'
              }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              _before={{ display: 'none' }}
              _after={{ display: 'none' }}
            >
              <HStack gap={2}>
                <Icon as={LuBuilding2} boxSize="18px" />
                <Text>Main Agents</Text>
              </HStack>
            </Tabs.Trigger>}
            <Tabs.Trigger
              value="branch"
              px={6}
              py={3}
              fontWeight="semibold"
              borderRadius="md"
              borderBottom="none"
              borderBottomWidth={0}
              borderBottomColor="transparent"
              boxShadow="none"
              _selected={{
                bg: 'orange.500',
                color: 'white',
                borderBottom: 'none',
                borderBottomWidth: 0,
                borderBottomColor: 'transparent',
                boxShadow: 'none'
              }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              _before={{ display: 'none' }}
              _after={{ display: 'none' }}
            >
              <HStack gap={2}>
                <Icon as={LuStore} boxSize="18px" />
                <Text>Branch Agents</Text>
              </HStack>
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        {/* Active Tab Badge */}
        <HStack mb={4}>
          <Badge
            colorPalette={activeTab === 'main' ? 'purple' : 'orange'}
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="sm"
          >
            <HStack gap={1}>
              <Icon as={activeTab === 'main' ? LuBuilding2 : LuStore} boxSize="14px" />
              <Text>{activeTab === 'main' ? 'Main Data' : 'Branch Data'}</Text>
            </HStack>
          </Badge>
          {activeTab === 'branch' && !canEditBranch() && (
            <Badge colorPalette="gray" variant="outline" px={2} py={1} borderRadius="full" fontSize="xs">
              View Only
            </Badge>
          )}
          {activeTab === 'main' && !canEditMain() && (
            <Badge colorPalette="gray" variant="outline" px={2} py={1} borderRadius="full" fontSize="xs">
              View Only
            </Badge>
          )}
        </HStack>

        <Card.Root shadow="xl" borderRadius="xl" borderTop="4px" borderTopColor={activeTab === 'main' ? 'purple.400' : 'orange.400'} mb={8}>
          <Card.Header pb={0}>
            <HStack justify="space-between">
              <HStack gap={2}>
                <Icon as={activeTab === 'main' ? LuBuilding2 : LuStore} color={activeTab === 'main' ? 'purple.500' : 'orange.500'} boxSize="20px" />
                <Heading size="md" color="gray.700">Filter {activeTab === 'main' ? 'Main' : 'Branch'} Agents</Heading>
              </HStack>
              <Icon as={FiFilter} color="gray.400" />
            </HStack>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} gap={6}>
              <Box>
                <Text fontWeight="600" color="gray.600" mb={2}>Country</Text>
                <Select
                  placeholder="Search countries..."
                  isClearable
                  options={countries.map(country => ({ value: country, label: country }))}
                  value={selectedCountryFilter ? { value: selectedCountryFilter, label: selectedCountryFilter } : null}
                  onChange={handleCountrySelect}
                  chakraStyles={{
                    container: (provided) => ({ ...provided, width: '100%' }),
                  }}
                  useBasicStyles
                  selectedOptionColorPalette="purple"
                />
              </Box>

              <Box>
                <Text fontWeight="600" color="gray.600" mb={2}>Agent Name</Text>
                <Select
                  placeholder="Search agents..."
                  isClearable
                  options={agents.map(agent => ({ value: agent, label: agent }))}
                  value={selectedAgentFilter ? { value: selectedAgentFilter, label: selectedAgentFilter } : null}
                  onChange={handleAgentSelect}
                  useBasicStyles
                  selectedOptionColorPalette="purple"
                />
              </Box>

              <Box>
                <Text fontWeight="600" color="gray.600" mb={2}>Uploader</Text>
                <Select
                  placeholder="Search uploaders..."
                  isClearable
                  options={uploaders.map(uploader => ({ value: uploader, label: uploader }))}
                  value={selectedUploaderFilter ? { value: selectedUploaderFilter, label: selectedUploaderFilter } : null}
                  onChange={handleUploaderSelect}
                  useBasicStyles
                  selectedOptionColorPalette="purple"
                />
              </Box>

              <Box>
                <Text fontWeight="600" color="gray.600" mb={2}>Status</Text>
                <Select
                  options={[
                    { label: 'Approved', value: 'approved' },
                    { label: 'Potential', value: 'potential' },
                    { label: 'All', value: 'all' }
                  ]}
                  value={{ label: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1), value: statusFilter }}
                  onChange={async (option) => {
                    const newStatus = option ? option.value : 'all';
                    setStatusFilter(newStatus);

                    // Build filters object with new status
                    const filters = {};
                    if (selectedCountryFilter) filters.country = selectedCountryFilter;
                    if (selectedAgentFilter) filters.agent = selectedAgentFilter;
                    if (selectedUploaderFilter) filters.uploader = selectedUploaderFilter;
                    if (newStatus !== 'all') filters.status = newStatus;

                    // Get the appropriate service based on active tab
                    const service = activeTab === 'branch' ? branchDataService : dataService;

                    // Always perform search when status changes
                    // If status='all' and no other filters, get all data
                    const hasEntityFilters = selectedCountryFilter || selectedAgentFilter || selectedUploaderFilter;

                    if (newStatus !== 'all' || hasEntityFilters) {
                      // Perform search with filters
                      setLoading(true);
                      setError(null);
                      resetResultsPagination();
                      try {
                        const response = await service.searchCombined(filters);
                        setRawSearchResults(response.data);
                        const filteredResults = applyServiceFilter(response.data, selectedService);
                        setSearchResults(filteredResults);
                      } catch (error) {
                        console.error('Error searching with status filter:', error);
                        setError('Failed to search data');
                        setSearchResults([]);
                        setRawSearchResults([]);
                      } finally {
                        setLoading(false);
                      }
                    } else if (newStatus !== 'all') {
                      // If no other filters but status is selected, search by status only
                      setLoading(true);
                      setError(null);
                      resetResultsPagination();
                      try {
                        const filters = { status: newStatus };
                        const response = await service.searchCombined(filters);
                        setRawSearchResults(response.data);
                        const filteredResults = applyServiceFilter(response.data, selectedService);
                        setSearchResults(filteredResults);
                      } catch (error) {
                        console.error('Error searching by status:', error);
                        setError('Failed to search data');
                        setSearchResults([]);
                        setRawSearchResults([]);
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      // Status is 'all' and no other filters, load all data
                      setLoading(true);
                      setError(null);
                      resetResultsPagination();
                      try {
                        const response = await service.getAllData();
                        setRawSearchResults(response.data);
                        const filteredResults = applyServiceFilter(response.data, selectedService);
                        setSearchResults(filteredResults);
                      } catch (error) {
                        console.error('Error loading all data:', error);
                        setError('Failed to load data');
                        setSearchResults([]);
                        setRawSearchResults([]);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  isClearable={false}
                  useBasicStyles
                  selectedOptionColorPalette="purple"
                />
              </Box>

              <Box>
                <Text fontWeight="600" color="gray.600" mb={2}>Service</Text>
                <Select
                  placeholder="Select service..."
                  isClearable
                  options={SERVICE_FILTER_OPTIONS}
                  value={selectedServiceOption}
                  onChange={handleServiceSelect}
                  useBasicStyles
                  selectedOptionColorPalette="purple"
                />
              </Box>
            </SimpleGrid>

            {/* Active Filter Display */}
            {(selectedCountryFilter || selectedAgentFilter || selectedUploaderFilter || selectedService) && (
              <Box mt={6} p={4} bg="purple.50" borderRadius="lg" border="1px" borderColor="purple.100">
                <Flex wrap="wrap" gap={3} align="center">
                  <Text fontWeight="bold" color="purple.600" fontSize="sm">Active Filters:</Text>
                  {selectedCountryFilter && (
                    <Badge colorScheme="purple" p={2} borderRadius="md" variant="subtle">🌍 Country: {selectedCountryFilter}</Badge>
                  )}
                  {selectedAgentFilter && (
                    <Badge colorScheme="blue" p={2} borderRadius="md" variant="subtle">🏢 Agent: {selectedAgentFilter}</Badge>
                  )}
                  {selectedUploaderFilter && (
                    <Badge colorScheme="orange" p={2} borderRadius="md" variant="subtle">👤 Uploader: {selectedUploaderFilter}</Badge>
                  )}
                  {selectedService && (
                    <Badge colorScheme="green" p={2} borderRadius="md" variant="subtle">🛠 Service: {selectedServiceOption?.label || selectedService.toUpperCase()}</Badge>
                  )}
                  <Spacer />
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={clearAllFilters}
                  >
                    <FiRefreshCw style={{ marginRight: '8px' }} /> Clear Filters
                  </Button>
                </Flex>
              </Box>
            )}
          </Card.Body>
        </Card.Root>

        {error && (
          <Box bg="red.50" color="red.500" p={4} borderRadius="md" mb={4}>
            {error}
          </Box>
        )}

        {loading && (
          <Flex justify="center" align="center" py={10}>
            <VStack>
              <Box className="spinner" />
              <Text color="gray.500">Searching...</Text>
            </VStack>
          </Flex>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Box mt={8}>
            <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={4}>
              <Box>
                {!hasActiveFilters && (
                  <Heading size="lg" color="purple.700" mb={1}>Search Results</Heading>
                )}
                <Text color="gray.600">
                  {searchResults.length} agent{searchResults.length !== 1 ? 's' : ''} found
                  {selectedServiceOption && (
                    <Text as="span" fontWeight="bold"> • Service: {selectedServiceOption.label}</Text>
                  )}
                </Text>
              </Box>

              {/* Export Controls */}
              {hasRole(['Admin']) && (
                <HStack spacing={2}>
                  <Select
                    options={getExportScopeOptions()}
                    value={getExportScopeOptions().find(opt => opt.value === exportScope)}
                    onChange={handleExportScopeSelect}
                    isDisabled={isExporting}
                    useBasicStyles
                    styles={{ container: (p) => ({ ...p, width: '200px' }) }}
                  />
                  <Select
                    options={exportFormatOptions}
                    value={exportFormatOptions.find(opt => opt.value === exportFormat)}
                    onChange={handleExportFormatResultsSelect}
                    isDisabled={isExporting}
                    useBasicStyles
                    styles={{ container: (p) => ({ ...p, width: '150px' }) }}
                  />
                  <Button
                    colorScheme="green"
                    onClick={handleExport}
                    isDisabled={isExporting || (exportScope === 'current' && searchResults.length === 0)}
                    isLoading={isExporting}
                    loadingText="Exporting"
                  >
                    <FiDownload style={{ marginRight: '8px' }} /> Export
                  </Button>
                </HStack>
              )}
            </Flex>

            {/* Export Progress */}
            {isExporting && (
              <Box mb={4}>
                <Box h="2px" bg="gray.100" w="full">
                  <Box h="full" bg="green.500" w={`${exportProgress}%`} transition="width 0.2s" />
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1}>{exportMessage}</Text>
              </Box>
            )}

            {/* Results Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} gap={6}>
              {getPaginatedSearchResults().map((result, index) => {
                const strongestServices = result.strongest_service
                  ? result.strongest_service.split(',').map(s => s.trim()).filter(s => s)
                  : [];
                return (
                  <Card.Root key={index} shadow="md" borderRadius="xl" _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }} transition="all 0.2s" borderTop="4px" borderTopColor={result.is_favorite ? 'yellow.400' : 'gray.200'}>
                    <Card.Body>
                      <Flex justify="space-between" align="start" mb={3}>
                        <Box flex={1} minW={0}>
                          <Heading size="md" mb={1} noOfLines={1} title={result.agent_name} color="gray.800">
                            {result.agent_name || 'No Name'}
                          </Heading>
                          <HStack spacing={2} wrap="wrap">
                            <Badge colorPalette="purple" variant="subtle" borderRadius="full" px={2} py={0.5} fontSize="xs">
                              {result.country}
                            </Badge>
                            {result.suspended && (
                              <Popover.Root positioning={{ placement: 'top' }}>
                                <Popover.Trigger asChild>
                                  <Badge colorPalette="red" variant="solid" borderRadius="full" px={2} py={0.5} fontSize="xs" cursor="pointer">
                                    Suspended
                                  </Badge>
                                </Popover.Trigger>
                                <Popover.Positioner>
                                  <Popover.Content bg="white" shadow="xl" borderRadius="lg" p={3} minW="220px">
                                    <Popover.Arrow>
                                      <Popover.ArrowTip />
                                    </Popover.Arrow>
                                    <Text fontSize="sm" fontWeight="bold" mb={1}>Suspended Remark</Text>
                                    <Text fontSize="sm" color="gray.700">
                                      {result.suspended_remark || 'No remark provided'}
                                    </Text>
                                  </Popover.Content>
                                </Popover.Positioner>
                              </Popover.Root>
                            )}
                          </HStack>
                        </Box>
                        {/* Favorite star - clickable for editors with appropriate permissions */}
                        {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            color={result.is_favorite ? 'yellow.400' : 'gray.300'}
                            onClick={(e) => handleToggleFavorite(result, e)}
                            ml={2}
                            flexShrink={0}
                            title={result.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                            fontSize="lg"
                          >
                            {result.is_favorite ? '⭐' : '☆'}
                          </Button>
                        ) : (
                          result.is_favorite && (
                            <Text fontSize="lg" color="yellow.400" ml={2}>⭐</Text>
                          )
                        )}
                      </Flex>

                      {(() => {
                        const services = [
                          { name: 'FCL', value: result.fcl, icon: LuContainer, isStrongest: strongestServices.includes('FCL') },
                          { name: 'LCL', value: result.lcl, icon: LuPackage, isStrongest: strongestServices.includes('LCL') },
                          { name: 'AIR', value: result.air, icon: LuPlane, isStrongest: strongestServices.includes('AIR') }
                        ];

                        const availableServices = services.filter((service) => getServiceAvailability(service.value));
                        const unavailableServices = services.filter((service) => !getServiceAvailability(service.value));

                        return (
                          <VStack align="stretch" spacing={2} mb={4}>
                            <Box>
                              <ConsolBadge
                                consolExport={result.consol_export}
                                consolImport={result.consol_import}
                                consolOversea={result.consol_oversea}
                              />
                            </Box>
                            <HStack wrap="wrap" spacing={2}>
                              {availableServices.map((service) => (
                                <React.Fragment key={service.name}>
                                  {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                </React.Fragment>
                              ))}
                              {getSpecialServiceBadge(result.special_service)}
                            </HStack>
                            {unavailableServices.length > 0 && (
                              <HStack wrap="wrap" spacing={2}>
                                {unavailableServices.map((service) => (
                                  <React.Fragment key={service.name}>
                                    {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                  </React.Fragment>
                                ))}
                              </HStack>
                            )}
                          </VStack>
                        );
                      })()}

                      <VStack align="stretch" spacing={2}>
                        <Button size="sm" variant="outline" colorPalette="purple" width="full" onClick={() => handleViewDetails(result)}>
                          SHOW DETAILS
                        </Button>
                        {/* Show edit buttons based on tab and role */}
                        {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                          <HStack>
                            <Button size="sm" flex={1} variant="outline" colorPalette={activeTab === 'main' ? 'purple' : 'orange'} onClick={() => handleEditAgent(result)}>EDIT</Button>
                            <Button size="sm" flex={1} variant="outline" colorPalette="orange" onClick={() => handleSendToQueue(result)}>QUEUE</Button>
                          </HStack>
                        )}
                        {hasRole(['Admin']) && (
                          <Button size="sm" colorPalette="red" variant="solid" onClick={() => handleDeleteAgent(result)}>DELETE</Button>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                );
              })}
            </SimpleGrid>

            {/* Pagination */}
            {searchResults.length > resultsItemsPerPage && (
              <Flex mt={8} justify="center" gap={2}>
                <Button onClick={handleResultsPrevPage} isDisabled={resultsCurrentPage === 1}>‹</Button>
                <Text alignSelf="center" color="gray.600" fontWeight="bold">Page {resultsCurrentPage} of {getTotalResultsPages()}</Text>
                <Button onClick={handleResultsNextPage} isDisabled={resultsCurrentPage === getTotalResultsPages()}>›</Button>
              </Flex>
            )}
          </Box>
        )}

        <Box mt={12} textAlign="center">
          <Button as={Link} to="/search" variant="link" colorScheme="purple" size="lg">
            View all Agents →
          </Button>
        </Box>
      </Container>

      {/* Modals */}
      {
        viewModalOpen && selectedAgent && (
          <ViewAgentDetailsModal
            agent={selectedAgent}
            onClose={handleCloseViewModal}
            formatDate={formatDate}
            dataType={activeTab}
          />
        )
      }

      {
        editModalOpen && selectedAgent && (
          <EditAgentModal
            agent={selectedAgent}
            onClose={handleCloseEditModal}
            onSave={handleSaveAgent}
            dataType={activeTab}
          />
        )
      }

      {
        sendToQueueModalOpen && selectedAgent && (
          <SendToQueueModal
            agent={selectedAgent}
            onClose={handleCloseSendToQueueModal}
            onConfirm={handleConfirmSendToQueue}
          />
        )
      }

      {
        specialServicesModalOpen && createPortal(
          <Dialog.Root open={true} onOpenChange={(e) => !e.open && setSpecialServicesModalOpen(false)}>
            <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(4px)" zIndex={9999} />
            <Dialog.Positioner zIndex={10000}>
              <Dialog.Content maxW="md" borderRadius="xl" boxShadow="2xl" bg="white" p={6}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md" color="purple.700" fontWeight="bold">Special Services</Heading>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setSpecialServicesModalOpen(false)}
                    aria-label="Close"
                  >
                    <FiX />
                  </IconButton>
                </Flex>
                <VStack align="stretch" spacing={2} maxH="60vh" overflowY="auto">
                  {selectedSpecialServices.length === 0 ? <Text color="gray.500">No special services</Text> :
                    selectedSpecialServices.map((s, i) => (
                      <HStack key={i} p={3} bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100" justify="space-between">
                        <HStack>
                          <Badge colorScheme="purple" borderRadius="full" boxSize="24px" display="flex" alignItems="center" justifyContent="center">
                            {i + 1}
                          </Badge>
                          <Text fontWeight="medium" color="gray.700">{s}</Text>
                        </HStack>
                        <Box as="svg" viewBox="0 0 20 20" fill="green" boxSize={5}>
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </Box>
                      </HStack>
                    ))
                  }
                </VStack>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Root>,
          document.body
        )
      }

      {/* Suggest Agent Modal for User role */}
      <SuggestAgentModal
        isOpen={suggestAgentModalOpen}
        onClose={() => setSuggestAgentModalOpen(false)}
      />
    </Box >
  );
};

export default Dashboard;
